import { useState } from "react";
import Datatable from "./Datatable";
import { handleApiData } from "../../store/actions";
import { useData, useDispatch } from "../../store/DataProvider";

const NEW_EXPENSE_TYPE = -1;

const ExpenseTypes = ({ expenseTypesDt }) => {
    const dispatch = useDispatch();
    const { expenseTypesObj } = useData();

    const [selectedExpenseTypeId, setSelectedExpenseTypeId] = useState();
    /* Kept apart from the selection on purpose. The form closes on save -- there
       is no way to dismiss it otherwise -- but the row it belonged to stays
       marked, so the place in a long list is not lost. */
    const [lastEditedId, setLastEditedId] = useState();
    const [thai, setThai] = useState();
    const [english, setEnglish] = useState();

    const formIsValid = thai && english;
    const isNew = Number(selectedExpenseTypeId) === NEW_EXPENSE_TYPE;
    const selected = isNew ? null : expenseTypesObj?.[selectedExpenseTypeId];

    /* Two records carrying the same name are how you say they are the same
       thing; the button becomes the offer to merge them. Everything else can
       only be deleted, and only while nothing is filed under it. */
    const duplicates = selected?.duplicates || 0;
    const expenseCount = selected?.expenseCount || 0;
    const canDedupe = duplicates > 1;

    /* The colour comes from a variable in App.css rather than being written in
       here: react-data-table-component takes style objects, not class names, so
       there is no CSS hook for "the selected row", but a var() keeps the palette
       in one place. */
    const rowStyles = [
        {
            when: row =>
                row._id === selectedExpenseTypeId || row._id === lastEditedId,
            style: {
                backgroundColor: "var(--row-selected)",
                fontWeight: "bold",
            },
        },
    ];

    const onClickRow = ({ _id }) => {
        setSelectedExpenseTypeId(_id);
        /* one row marked at a time */
        setLastEditedId(null);
        if (Number(_id) === NEW_EXPENSE_TYPE) {
            setThai(null);
            setEnglish(null);
            return;
        }
        const expenseType = expenseTypesObj[_id];
        setThai(expenseType?.thai);
        setEnglish(expenseType?.english);
    };

    const resetFormFields = () => {
        setSelectedExpenseTypeId(null);
        const funcs = [setThai, setEnglish];
        funcs.forEach(func => func(null));
    };

    /* nothing to go back to once a record has been merged away or deleted */
    const clearAll = () => {
        setLastEditedId(null);
        resetFormFields();
    };

    const onClickSubmit = () => {
        /* safety measure - shouldn't be possible */
        if (!formIsValid) {
            window.alert("Please make sure all fields are valid.");
            return;
        }
        const data = {
            expenseTypeId: selectedExpenseTypeId,
            thai,
            english,
        };
        /* the handler returns the rebuilt list with its reply, so there is
           nothing to fetch again here and nothing to race */
        handleApiData(data, dispatch, "saveExpenseType");
        /* The form closes, and the row it belonged to stays marked so the place
           in the list is kept. A new type has no id to mark -- the one Mongo
           assigned is not known here. */
        if (!isNew) {
            setLastEditedId(selectedExpenseTypeId);
        }
        resetFormFields();
    };

    const onClickDelete = () => {
        const name = `${selected.thai} (${selected.english})`;
        if (
            !window.confirm(
                `Delete ${name}?\n\nNothing is filed under it. This cannot be undone.`,
            )
        ) {
            return;
        }
        handleApiData(
            { expenseTypeId: selectedExpenseTypeId },
            dispatch,
            "deleteExpenseType",
        );
        clearAll();
    };

    const onClickDedupe = () => {
        const name = `${selected.thai} (${selected.english})`;
        const moving = expenseTypesDt.data.reduce(
            (acc, row) =>
                row.expenseType === name ? acc + (row.expenseCount || 0) : acc,
            0,
        );
        const e = moving === 1 ? "" : "s";
        if (
            !window.confirm(
                `Merge ${duplicates} records named ${name} into one?\n\n` +
                    `${moving} expense${e} will move onto the record that is kept. ` +
                    `This cannot be undone.`,
            )
        ) {
            return;
        }
        handleApiData(
            { expenseTypeId: selectedExpenseTypeId },
            dispatch,
            "dedupeExpenseType",
        );
        clearAll();
    };

    return (
        <div className="data-wrapper expense-types-pane">
            <div className="expense-types-title">
                Edit / Enter Expense Types
            </div>

            <div className="expense-types-list">
                <Datatable
                    settings={expenseTypesDt}
                    clickFunc={onClickRow}
                    conditionalRowStyles={rowStyles}
                />
            </div>

            {selectedExpenseTypeId ? (
                <div className="admin-staff-container">
                    <div className="admin-staff-row">
                        <div className="admin-input-container">
                            <div className="admin-staff-label">
                                Thai:
                            </div>
                            <input
                                className="admin-staff-input"
                                type="text"
                                placeholder="Pasah Thai"
                                value={thai ? thai : ""}
                                onChange={e => setThai(e.target.value)}
                            />
                        </div>
                        <div className="admin-input-container">
                            <div className="admin-staff-label">
                                English:
                            </div>
                            <input
                                className="admin-staff-input"
                                type="text"
                                placeholder="English language"
                                value={english ? english : ""}
                                onChange={e => setEnglish(e.target.value)}
                            />
                        </div>
                    </div>

                    <div
                        className="admin-staff-row"
                        style={{ marginTop: "5px" }}
                    >
                        <div className="admin-input-container">
                            <button
                                type="button"
                                className="admin-staff-button"
                                disabled={!formIsValid}
                                onClick={onClickSubmit}
                                style={{
                                    opacity: formIsValid ? 1 : ".4",
                                }}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>

                    {isNew ? null : (
                        <div className="admin-orphan-row">
                            {canDedupe ? (
                                <button
                                    type="button"
                                    className="admin-dedupe-button"
                                    onClick={onClickDedupe}
                                >
                                    Dedupe
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="admin-orphan-button"
                                    onClick={onClickDelete}
                                    disabled={expenseCount > 0}
                                    style={{
                                        opacity: expenseCount > 0 ? 0.4 : 1,
                                        cursor:
                                            expenseCount > 0
                                                ? "default"
                                                : "pointer",
                                    }}
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    )}

                    {isNew || canDedupe || !expenseCount ? null : (
                        <div className="admin-type-note">
                            {expenseCount === 1
                                ? "1 expense is filed under this type"
                                : `${expenseCount} expenses are filed under this type`}
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default ExpenseTypes;
